import { snakeCase } from 'change-case';
import castArray from 'lodash/castArray.js';
import find from 'lodash/find.js';
import findIndex from 'lodash/findIndex.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import reject from 'lodash/reject.js';

class QueryBuilder {
    constructor(endpoint) {
        this.baseUrl = '/api';

        this.setEndpoint(endpoint);

        this._includes = [];
        this._filters = [];
        this._fields = [];
        this._appends = [];
        this._sorts = [];
        this._custom_filters = [];
        this._pagination = null;

        this._raw = {};
        this._arrayRaw = {};
        this._sortByResponse = false;
        this._sortIdOrder = [];
        this._response = null;
    }

    withoutBase() {
        this.baseUrl = '';

        return this;
    }

    withMenu() {
        console.warn('QueryBuilder.withMenu() is deprecated. Use MenuService.loadAndSetCourseMenu() instead.');
        this.raw({ menu: 'true' });

        return this;
    }

    setEndpoint(endpoint) {
        this.endpoint = endpoint;

        return this;
    }

    addItems(type, items) {
        castArray(items).forEach(item => this[`_${type}`].push(item));
    }

    include(includes) {
        this.addItems('includes', includes);

        return this;
    }

    sortedByResponse(models) {
        if (!models || models.length === 0 || !this._response) {
            return [];
        }

        this._sortIdOrder = this._response.response.data.data.map(model => model.id);

        if (this._sortIdOrder.length < 2) {
            return models;
        }

        const sorted = [];

        this._sortIdOrder.forEach(id => {
            sorted.push(find(models, { id }));
        });

        return sorted;
    }

    applyToModel(model, pagination) {
        const query = model.query();

        if (this._includes.length) {
            const relationships = this._includes.map(nestedRelationship =>
                nestedRelationship
                    .split('.')
                    .map(relationship => snakeCase(relationship))
                    .join('.')
            );

            query.with(relationships);
        }

        if (this._sorts && !this._sortByResponse) {
            this._sorts.forEach(sort => {
                if (sort[0] === '-') {
                    query.orderBy(sort.substr(1), 'desc');
                } else {
                    query.orderBy(sort, 'asc');
                }
            });
        }

        if (this.hasTagSearch()) {
            query.whereHas('tags', tagQuery => {
                tagQuery.where(tag => tag.name.en === this._raw.tags);
            });
        }

        if (this.hasCustomFilters()) {
            this._custom_filters.forEach(({ filter, value, field }) => {
                filter.applyToModel(query, value, field);
            });
        }

        if (this.hasGroupSearch()) {
            query.whereHas('groups', groupQuery => {
                groupQuery.where(group => group.name === this._raw.groups);
            });
        }

        if (this.hasSearch()) {
            if (!Object.hasOwn(model, 'searchable')) {
                throw new Error('Model must implement a searchable() method specifying which keys are searchable.');
            }

            if (['course_enrollments', 'attempt_authorizations'].includes(model.entity)) {
                query.whereHas('user', userQuery => {
                    userQuery.search(this._raw.search, {
                        keys: model.searchable(),
                    });
                });
            } else {
                query.search(this._raw.search, { keys: model.searchable() });
            }
        }

        this._filters.forEach(filter => {
            if (get(filter, 'options.ignoreFromModel')) {
                return;
            }

            if (get(filter, 'options.custom')) {
                query.where(filter.options.custom);
                return;
            }

            query.where(filter.key, filter.value);
        });

        if (pagination?.entity_ids) {
            query.whereIdIn(pagination.entity_ids);
        }

        return query;
    }

    getFilter(key) {
        return this._filters.find(filter => filter.key === key);
    }

    removeFilter(key) {
        this._filters = reject(this._filters, filter => filter.key === key);

        return this;
    }

    hasFilter(key) {
        return !!this._filters.find(filter => filter.key === key);
    }

    customFilter(filter) {
        this._custom_filters.push(filter);
    }

    filter(key, value, options) {
        if (this.hasFilter(key)) {
            this.removeFilter(key);
        }

        this.addItems('filters', { key, value, options });

        return this;
    }

    filters(filters) {
        this.addItems('filters', filters);

        return this;
    }

    fields(fields) {
        this.addItems('fields', fields);

        return this;
    }

    append(appends) {
        this.addItems('appends', appends);

        return this;
    }

    sort(sorts, direction) {
        if (!direction || direction === 'asc') {
            this.addItems('sorts', sorts);
        } else {
            this.sortDesc(sorts);
        }

        return this;
    }

    sortDesc(sorts) {
        this.addItems(
            'sorts',
            castArray(sorts).map(sort => `-${sort}`)
        );

        return this;
    }

    clearSorts() {
        this._sorts = [];

        return this;
    }

    clearFilters() {
        this._filters = [];

        return this;
    }

    search(query) {
        if (!query) {
            return this.removeSearch();
        }

        this.raw({ search: query });

        return this;
    }

    removeSearch() {
        this.removeRaw('search');

        return this;
    }

    removeCustomFilter(field) {
        const index = findIndex(this._custom_filters, customFilter => customFilter.field === field);

        if (index !== -1) {
            this._custom_filters.splice(index, 1);
        }
    }

    provideResponse(response) {
        this._response = response;

        return this;
    }

    menu() {
        console.warn('QueryBuilder.menu() is deprecated. Use MenuService.loadAndSetCourseMenu() instead.');
        this.raw({ menu: 'true' });

        return this;
    }

    array(field, value) {
        this._arrayRaw[field] = value;

        return this;
    }

    raw(params) {
        this._raw = {
            ...this._raw,
            ...params,
        };

        return this;
    }

    hasRaw(key) {
        return has(this._raw, key);
    }

    removeRaw(key) {
        delete this._raw[key];
    }

    removeArray(key) {
        delete this._arrayRaw[key];
    }

    page(page) {
        return this.setPagination({ page });
    }

    currentPage() {
        return this._pagination?.page;
    }

    pageSize(size) {
        return this.raw({ per_page: size });
    }

    setPagination(pagination) {
        this._pagination = pagination;

        return this;
    }

    when(truth, handler) {
        if (truth) {
            handler(this);
        }

        return this;
    }

    hasSearch() {
        return Object.hasOwn(this._raw, 'search');
    }

    hasTagSearch() {
        return Object.hasOwn(this._raw, 'tags');
    }

    hasGroupSearch() {
        return Object.hasOwn(this._raw, 'groups');
    }

    hasCustomFilters() {
        return this._custom_filters.length > 0;
    }

    getCustomFilter(field) {
        return this._custom_filters.find(customFilter => customFilter.field === field);
    }

    hasItems() {
        return (
            this._includes.length +
                this._filters.length +
                this._appends.length +
                this._sorts.length +
                this._fields.length +
                Object.keys(this._raw).length >
            0
        );
    }

    toUrl(id = null) {
        let url = this.endpoint.includes('/api') ? this.endpoint : `${this.baseUrl}/${this.endpoint}`;

        // Append ID if provided
        if (id) {
            url += `/${id}`;
        }

        const params = {};

        if (this._includes.length) {
            params.include = this._includes.join(',');
        }

        if (this._filters.length) {
            this._filters.forEach(({ key, value }) => {
                const normalizedValue = Array.isArray(value) ? [...value] : value;

                params[`filter[${key}]`] = normalizedValue;
            });
        }

        if (this._custom_filters.length) {
            this._custom_filters.forEach(({ filter, field, value }) => {
                if (filter.applyToParams) {
                    filter.applyToParams(params, value, field);
                } else {
                    params[field] = value;
                }
            });
        }

        if (this._fields.length) {
            this._fields.forEach(({ key, value }) => {
                params[`fields[${key}]`] = value;
            });
        }

        if (this._appends.length) {
            params.append = this._appends.join(',');
        }

        if (this._sorts.length) {
            params.sort = this._sorts.join(',');
        }

        if (Object.keys(this._raw).length) {
            Object.keys(this._raw).forEach(key => {
                params[key] = this._raw[key];
            });
        }

        if (this._pagination) {
            params.page = this._pagination.page;
        }

        let query = Object.keys(params)
            .flatMap(key => {
                const value = params[key];
                if (Array.isArray(value)) {
                    // Filter out null/undefined values and create separate query params for each array item
                    const filteredItems = value.filter(item => item !== null && item !== undefined);
                    return filteredItems.length > 0
                        ? filteredItems.map(item => `${key}[]=${encodeURIComponent(item)}`)
                        : [];
                }
                // Skip null/undefined values for non-array parameters
                if (value === null || value === undefined) {
                    return [];
                }

                // Special handling for parameters that contain comma-separated values
                // These should not have their commas encoded
                const commaDelimitedParams = ['include', 'append', 'sort'];
                const isCommaDelimited = commaDelimitedParams.includes(key) || key.startsWith('fields[');

                if (isCommaDelimited) {
                    // For comma-delimited params, encode individual parts but keep commas
                    const parts = String(value).split(',');
                    const encodedParts = parts.map(part => encodeURIComponent(part.trim()));
                    return [`${key}=${encodedParts.join(',')}`];
                }

                // Return array containing the parameter string (flatMap expects arrays)
                return [`${key}=${encodeURIComponent(value)}`];
            })
            .filter(param => param.length > 0)
            .join('&');

        if (Object.keys(this._arrayRaw).length) {
            let isFirstArrayParam = query.length === 0;

            Object.keys(this._arrayRaw).forEach(key => {
                this._arrayRaw[key].forEach(value => {
                    const concatenator = isFirstArrayParam ? '' : '&';

                    query += `${concatenator}${key}[]=${value}`;

                    isFirstArrayParam = false;
                });
            });
        }

        const join = url.includes('?') ? '&' : '?';

        return query ? `${url}${join}${query}` : url;
    }
}

export default QueryBuilder;
